import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { downloadInvoicePdf } from "@/lib/invoice";

const STATUS_TONE = {
  pending: "text-brand-neutral",
  awaiting_payment: "text-[#B8860B]",
  paid: "text-brand-accent",
  ready_for_collection: "text-brand-accent",
  out_for_delivery: "text-brand-accent",
  completed: "text-brand-dark",
  cancelled: "text-red-700",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function WholesaleAccount() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/login");
      return;
    }
    if (user.role !== "wholesale" || user.status !== "approved") {
      // Non-retailer users don't have a portal — send them home.
      nav("/");
      return;
    }
    api
      .get("/wholesale/orders")
      .then((r) => setOrders(r.data || []))
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Could not load your orders.");
      })
      .finally(() => setFetching(false));
  }, [user, loading, nav]);

  if (loading || fetching) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 pt-24 pb-24 text-brand-neutral">
        Loading your account…
      </div>
    );
  }

  const totalPaid = orders
    .filter((o) => ["paid", "ready_for_collection", "out_for_delivery", "completed"].includes(o.status))
    .reduce((s, o) => s + (o.total || 0), 0);
  const totalPending = orders
    .filter((o) => !["completed", "cancelled", "paid", "ready_for_collection", "out_for_delivery"].includes(o.status))
    .reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div data-testid="wholesale-account-page" className="fade-up">
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
        <p className="overline">Retailer account</p>
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-serif font-light text-3xl md:text-5xl text-brand-dark leading-[1.1]">
              Welcome, <span className="italic">{user?.name?.split(" ")[0] || "retailer"}</span>.
            </h1>
            <p className="mt-3 text-brand-neutral">
              {user?.business_name ? user.business_name : "Your Island Studio account."}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <Link
              data-testid="place-order-link"
              to="/wholesale"
              className="text-sm underline underline-offset-4 text-brand-accent"
            >
              Place a new order
            </Link>
            <button
              data-testid="account-logout"
              onClick={() => {
                logout();
                nav("/");
              }}
              className="text-sm underline underline-offset-4 text-brand-neutral hover:text-brand-dark"
            >
              Sign out
            </button>
          </div>
        </div>
      </section>

      {/* Summary strip */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-8 border-y border-brand-border py-8">
          <div>
            <p className="overline text-[10px]">Orders placed</p>
            <p data-testid="stat-order-count" className="font-serif text-3xl mt-2 text-brand-dark">
              {orders.length}
            </p>
          </div>
          <div>
            <p className="overline text-[10px]">Paid to date</p>
            <p data-testid="stat-lifetime-spend" className="font-serif text-3xl mt-2 text-brand-dark">
              ${totalPaid.toFixed(2)}
            </p>
            {totalPending > 0 && (
              <p className="text-xs text-brand-neutral mt-1">
                ${totalPending.toFixed(2)} pending
              </p>
            )}
          </div>
          <div className="col-span-2 md:col-span-1">
            <p className="overline text-[10px]">Account status</p>
            <p className="font-serif text-lg mt-2 text-brand-accent capitalize">
              {user?.status || "approved"} · wholesale
            </p>
          </div>
        </div>
      </section>

      {/* Order history */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pt-14 pb-24">
        <div className="flex items-center justify-between mb-8">
          <p className="overline">Order history</p>
          <p className="text-sm text-brand-neutral">
            {orders.length} {orders.length === 1 ? "order" : "orders"}
          </p>
        </div>

        {orders.length === 0 ? (
          <div
            data-testid="orders-empty"
            className="border-y border-brand-border py-16 text-center text-brand-neutral"
          >
            <p>You haven't placed an order yet.</p>
            <Link
              to="/wholesale"
              className="mt-4 inline-block underline underline-offset-4 text-brand-accent"
            >
              Browse the wholesale catalogue
            </Link>
          </div>
        ) : (
          <ul data-testid="orders-list" className="divide-y divide-brand-border border-y border-brand-border">
            {orders.map((o) => {
              const itemCount = (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
              return (
                <li
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  className="py-6 grid grid-cols-2 md:grid-cols-6 gap-y-3 gap-x-6 items-center"
                >
                  <div className="col-span-2 md:col-span-2">
                    <p className="font-serif text-lg text-brand-dark">
                      #{o.id.slice(0, 8).toUpperCase()}
                    </p>
                    <p className="text-xs text-brand-neutral mt-1">
                      {formatDate(o.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="overline text-[10px]">Items</p>
                    <p className="text-sm text-brand-dark mt-1">{itemCount}</p>
                  </div>
                  <div>
                    <p className="overline text-[10px]">Total</p>
                    <p className="text-sm text-brand-dark mt-1">
                      ${(o.total || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <p className="overline text-[10px]">Status</p>
                    <p
                      className={`text-sm mt-1 ${STATUS_TONE[o.status] || "text-brand-dark"}`}
                    >
                      {o.status_label || o.status}
                    </p>
                  </div>
                  <div className="col-span-2 md:col-span-1 md:text-right flex items-center gap-4 md:justify-end">
                    <button
                      data-testid={`download-invoice-${o.id}`}
                      onClick={() =>
                        downloadInvoicePdf({
                          path: `/wholesale/orders/${o.id}/invoice.pdf`,
                          filename: `island-studio-invoice-${o.id.slice(0, 8).toUpperCase()}.pdf`,
                        })
                      }
                      aria-label="Download invoice PDF"
                      title="Download invoice PDF"
                      className="text-brand-neutral hover:text-brand-accent"
                    >
                      <Download strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`toggle-order-${o.id}`}
                      onClick={() =>
                        setOrders((prev) =>
                          prev.map((x) =>
                            x.id === o.id ? { ...x, _open: !x._open } : x
                          )
                        )
                      }
                      className="text-sm underline underline-offset-4 text-brand-accent"
                    >
                      {o._open ? "Hide details" : "View details"}
                    </button>
                  </div>

                  {o._open && (
                    <div
                      data-testid={`order-detail-${o.id}`}
                      className="col-span-2 md:col-span-6 bg-[#F4F2EC] p-6 mt-2"
                    >
                      <p className="overline text-[10px]">Items ordered</p>
                      <ul className="mt-3 divide-y divide-brand-border">
                        {(o.items || []).map((i, idx) => (
                          <li key={idx} className="py-2 flex items-center justify-between gap-4 text-sm">
                            <span className="text-brand-dark">
                              {i.qty} × {i.title || i.slug}
                            </span>
                            <span className="text-brand-neutral">
                              ${((i.price || 0) * (i.qty || 0)).toFixed(2)}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 grid grid-cols-2 gap-y-1 text-sm max-w-sm ml-auto">
                        <span className="text-brand-neutral">Subtotal</span>
                        <span className="text-right">${(o.subtotal || 0).toFixed(2)}</span>
                        <span className="text-brand-neutral">Delivery</span>
                        <span className="text-right">
                          ${(o.delivery_fee || 0).toFixed(2)}
                          {o.delivery_tbc ? " (TBC)" : ""}
                        </span>
                        <span className="text-brand-neutral">VAT (10%)</span>
                        <span className="text-right">${(o.vat || 0).toFixed(2)}</span>
                        <span className="text-brand-dark font-medium pt-2 border-t border-brand-border">
                          Total
                        </span>
                        <span className="text-right text-brand-dark font-medium pt-2 border-t border-brand-border">
                          ${(o.total || 0).toFixed(2)}
                        </span>
                      </div>

                      {o.notes && (
                        <p className="mt-4 text-xs text-brand-neutral">
                          <b>Notes:</b> {o.notes}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
